import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Search } from 'lucide-react';

import { useLotStore } from '@/stores/useLot.store';

import { Card } from '@/components/shadcn-ui/card';
import { Input } from '@/components/shadcn-ui/input';
import Loader from '@/components/sharedComponents/Loader';
import DryingLotCard from '@/components/drying-lots/DryingLotCard';

import DryingBedsListSection from './DryingBedsListSection';

import { cn } from '@/lib/utils';
import { useMasterStore } from '@/stores/useMaster.store';
import { useDebounceValue } from '@/hooks/useDebouncedValue';

const LIMIT = 20;

const DryingLotsPage = () => {
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');
  const [section, setSection] = useState('DryingLots');

  const fetchDryingBeds = useMasterStore(state => state.fetchDryingBeds);
  const fetchDryingLots = useLotStore(state => state.fetchDryingLots);
  const dryingLots = useLotStore(state => state.dryingLots);
  const pagination = useLotStore(state => state.pagination);

  // Refs to avoid re-creating observer
  const observerRef = useRef(null);
  const loadingRef = useRef(false);
  const paginationRef = useRef(pagination);
  const fetchDryingLotsRef = useRef(fetchDryingLots);
  const queryRef = useRef('');

  /* ---------------- keep refs in sync ---------------- */
  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  useEffect(() => {
    fetchDryingLotsRef.current = fetchDryingLots;
  }, [fetchDryingLots]);

  /* ---------------- initial load ---------------- */
  useEffect(() => {
    fetchDryingBeds();
  }, [fetchDryingBeds]);

  /* ---------------- search + page 1 fetch ---------------- */
  const debouncedQuery = useDebounceValue(query, 300);

  useEffect(() => {
    queryRef.current = debouncedQuery || '';
    setPage(1);

    loadingRef.current = true;
    setLoading(true);

    fetchDryingLotsRef.current(queryRef.current, 1, LIMIT).finally(() => {
      loadingRef.current = false;
      setLoading(false);
    });
  }, [debouncedQuery]);

  /* ---------------- next page fetch ---------------- */
  useEffect(() => {
    if (page === 1) return;
    if (loadingRef.current) return;

    loadingRef.current = true;
    setLoading(true);

    fetchDryingLotsRef.current(queryRef.current, page, LIMIT).finally(() => {
      loadingRef.current = false;
      setLoading(false);
    });
  }, [page]);

  /* ---------------- intersection observer ---------------- */
  const observerCallbackRef = useCallback(node => {
    if (observerRef.current) observerRef.current.disconnect();
    if (!node) return;

    observerRef.current = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && !loadingRef.current && paginationRef.current?.hasNextPage) {
          setPage(prev => prev + 1);
        }
      },
      { rootMargin: '150px' }
    );

    observerRef.current.observe(node);
  }, []);

  return (
    <div className="flex h-full flex-col space-y-[18px] overflow-hidden px-4 py-[18px]">
      <Card className="flex h-[50px] max-h-[50px] items-center gap-0 rounded-[8px] px-1 py-1.5">
        <button
          className={cn('h-full flex-1 rounded-[8px] py-2 font-medium', section === 'DryingLots' && 'bg-[#F3F4F6]')}
          onClick={() => setSection('DryingLots')}
        >
          Drying Lots
        </button>
        <button
          className={cn('h-full flex-1 rounded-[8px] py-2 font-medium', section === 'DryingBeds' && 'bg-[#F3F4F6]')}
          onClick={() => setSection('DryingBeds')}
        >
          Drying Beds
        </button>
      </Card>

      {section === 'DryingLots' ? (
        <div className="flex min-h-0 flex-1 flex-col">
          {/* Search Input (fixed height) */}
          <div className="relative h-11 flex-shrink-0">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search lots..."
              className="h-full rounded-[8px] bg-white pl-9 text-base"
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </div>

          <div className="mt-3 flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
            {dryingLots?.length > 0 ? (
              <>
                {dryingLots.map(lot => (
                  <DryingLotCard key={lot._id} lot={lot} />
                ))}

                {pagination?.hasNextPage && <div ref={observerCallbackRef} className="h-4" />}

                {loading && (
                  <div className="flex justify-center py-4">
                    <Loader />
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-1 items-center justify-center">
                <p className="text-sm text-muted-foreground">No lots found</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="overflow-y-auto">
          <DryingBedsListSection />
        </div>
      )}
    </div>
  );
};

export default DryingLotsPage;
